import { useEffect, useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { apiFetch } from '../../lib/api';
import type { HtmlCheckResponse } from '../../types/api';

/**
 * Asks the server what it makes of this HTML (`POST /templates/html-check`):
 * translated Mailchimp tags, problems that block saving, what the sanitiser
 * removes, images, footer rules, and the preview HTML. Debounced so typing
 * in the source editor does not send a request per key.
 */
export function useHtmlCheck(html: string, enabled = true, delayMs = 400) {
  const [debounced, setDebounced] = useState(html);
  useEffect(() => {
    if (html === debounced) return;
    // The first text after an empty editor (a paste, a file) is checked at once.
    const timer = window.setTimeout(() => setDebounced(html), debounced === '' ? 0 : delayMs);
    return () => window.clearTimeout(timer);
  }, [html, debounced, delayMs]);

  const query = useQuery({
    queryKey: ['html-check', debounced],
    queryFn: ({ signal }) =>
      apiFetch<HtmlCheckResponse>('/templates/html-check', {
        method: 'POST',
        body: { html: debounced },
        signal,
      }),
    enabled,
    gcTime: 0,
    staleTime: Infinity,
    retry: false,
    placeholderData: keepPreviousData,
  });

  return {
    report: query.data?.report ?? null,
    /** True while the shown report is for older text than the editor holds. */
    pending: enabled && (debounced !== html || query.isFetching),
    isError: query.isError,
  };
}
